import { useMutation, useQuery } from '@tanstack/react-query';
import { ImageOff, Images, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { ZgrajZAutomatu } from '@/components/ZgrajZAutomatu';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { fetchPokrycieObrazkow, zgrajObrazkiZAutomatu } from '@/lib/items-api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  OBRAZKI GRUP, KTÓRYCH PORTAL NIE MA — i pobranie ich z automatu na ekranie grup.
 *
 *  ⚠️⚠️ ZGŁOSZONE 27.10.2026 przy pierwszym obejrzeniu: „mamy też pozycje, gdzie nie ma zdjęcia
 *  i nie ma opcji pobrania go w górę". Grupy klienta wskazują pliki od lat (`ItemGroups.IMAGEFILE`),
 *  a biblioteka portalu powstaje ze zgrania — więc zaraz po wdrożeniu prawie każdy kafelek był
 *  pusty, a jedyna droga do zgrania stała w „Zarządzaj zdjęciami" na liście artykułów.
 *
 *  ⚠️ TO SAMO ZGRANIE, NIE DRUGIE: `POST /items/obrazki/zgraj` od 27.10.2026 bierze też pliki grup,
 *  a okno wyboru maszyny (`ZgrajZAutomatu`) jest tym samym oknem co w „Zarządzaj zdjęciami".
 *  Zgrywa wszystko, czego portal nie ma — także brakujące zdjęcia artykułów, bo to jedna biblioteka.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function BrakujaceObrazkiGrup({
  groupId,
  brakuje,
  onZlecono,
}: {
  groupId: string;
  /** Grupy i podgrupy, które wskazują plik, którego biblioteka nie ma. */
  brakuje: number;
  /** Zgranie zlecone — ekran grup odpytuje drzewo, dopóki pliki nie dojadą. */
  onZlecono: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [wybor, setWybor] = useState(false);

  /** Spis plików na maszynach — z niego okno podpowiada automat z największą liczbą plików. */
  const pokrycie = useQuery({
    queryKey: ['pokrycie-obrazkow', groupId],
    queryFn: () => fetchPokrycieObrazkow(groupId),
    enabled: wybor,
  });

  const zgraj = useMutation({
    mutationFn: (agentId: string) => zgrajObrazkiZAutomatu(groupId, agentId),
    onSuccess: (r) => {
      setWybor(false);
      toast.success(
        r.zadan === 0
          ? t('obraz.zgraneNicNowego').replace('{n}', String(r.juzMamy))
          : t('obraz.zgraneZlecone').replace('{plikow}', String(r.plikow)).replace('{wezel}', r.wezel),
      );
      if (r.zadan > 0) onZlecono();
    },
    onError: (e: Error) => toast.error(e instanceof ApiError ? e.message : String(e)),
  });

  if (brakuje === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 px-3 py-2 text-sm">
      <ImageOff className="size-4 shrink-0 text-[var(--warning)]" />
      <span>{t('igrp.brakObrazkow').replace('{n}', String(brakuje))}</span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="ml-auto"
        disabled={zgraj.isPending || (wybor && pokrycie.isPending)}
        onClick={() => setWybor(true)}
      >
        {wybor && pokrycie.isPending ? <Loader2 className="animate-spin" /> : <Images />}
        {t('obraz.zgraj')}
      </Button>

      {wybor && pokrycie.data && (
        <ZgrajZAutomatu
          dane={pokrycie.data}
          pracuje={zgraj.isPending}
          onZgraj={(agentId) => zgraj.mutate(agentId)}
          onClose={() => setWybor(false)}
        />
      )}
    </div>
  );
}
